import gql from 'graphql-tag';
import { defineStore, storeToRefs } from 'pinia';
import { useI18n } from 'vue-i18n';
import apolloClient from '@/apollo/client';
import { modal } from '@/helpers/ui';
import { useRegisterStore } from '@/modules/register/stores/register';
import { useLocationsStore } from '@/stores/locations';

export const useRegisterDrawerStore = defineStore('register/drawer', () => {
  const currentCashupId = ref<string | null>(null);
  const previousCashFloatAmount = ref(0);
  const drawerStatusLocationId = ref<number | null>(null);
  const { locationId: currentLocationId } = storeToRefs(useLocationsStore());
  const { cashupEnabled } = storeToRefs(useRegisterStore());

  const isLoaded = computed(() => drawerStatusLocationId.value !== null);
  const isOpen = computed(() => !!currentCashupId.value);
  const isLoadingDrawerStatus = computed(
    () =>
      cashupEnabled.value &&
      !!currentLocationId.value &&
      drawerStatusLocationId.value !== currentLocationId.value,
  );
  const isCurrentLocation = (locationId?: number) =>
    locationId === currentLocationId.value;
  const { t } = useI18n();

  const getDrawerStatus = () => {
    const { locationId } = useLocationsStore();

    return apolloClient
      .query({
        query: gql`
          query getLocation($id: Int!) {
            location(id: $id) {
              openCashup {
                id
              }
              previousCashFloatAmount
            }
          }
        `,
        variables: {
          id: locationId,
        },
      })
      .then(({ data: { location } }) => {
        if (!isCurrentLocation(locationId)) {
          return;
        }

        currentCashupId.value = location.openCashup?.id || null;
        previousCashFloatAmount.value = location.previousCashFloatAmount || 0;
        drawerStatusLocationId.value = locationId;
      })
      .catch(() => {
        if (!isCurrentLocation(locationId)) {
          return;
        }

        currentCashupId.value = null;
        previousCashFloatAmount.value = 0;
        drawerStatusLocationId.value = locationId;
      });
  };

  watch(
    [currentLocationId, cashupEnabled],
    () => {
      if (cashupEnabled.value && currentLocationId.value) {
        getDrawerStatus();
      }
    },
    { immediate: true },
  );

  const openCashup = (cashFloatAmount: number) => {
    const { locationId } = useLocationsStore();

    return new Promise<void>((resolve) => {
      apolloClient
        .mutate({
          mutation: gql`
            mutation openCashup($input: OpenCashupInput!) {
              openCashup(input: $input) {
                cashup {
                  id
                }
                error {
                  type
                }
              }
            }
          `,
          variables: {
            input: {
              cashFloatAmount,
              locationId,
            },
          },
        })
        .then(
          ({
            data: {
              openCashup: { cashup, error },
            },
          }) => {
            if (error?.type === 'location_already_has_open_cashup') {
              modal('warning', {
                message: t('register.drawer_already_open'),
              });
            }
            if (isCurrentLocation(locationId)) {
              currentCashupId.value = cashup.id;
            }
            resolve();
          },
        );
    });
  };

  const closeCashup = (formData: any) => {
    const { locationId } = useLocationsStore();

    return new Promise<void>((resolve) => {
      apolloClient
        .mutate({
          mutation: gql`
            mutation closeCashup($input: CloseCashupInput!) {
              closeCashup(input: $input) {
                cashup {
                  cashFloatAmount
                  id
                }
              }
            }
          `,
          variables: {
            input: {
              ...formData,
              locationId,
            },
          },
        })
        .then(
          ({
            data: {
              closeCashup: { cashup },
            },
          }) => {
            if (isCurrentLocation(locationId)) {
              currentCashupId.value = null;
              previousCashFloatAmount.value = cashup.cashFloatAmount || 0;
            }
            resolve();
          },
        );
    });
  };

  return {
    currentCashupId,
    previousCashFloatAmount,
    isLoaded,
    isOpen,
    isLoadingDrawerStatus,
    getDrawerStatus,
    openCashup,
    closeCashup,
  };
});
